import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Target, Zap, ArrowRight, Newspaper, Check } from "lucide-react";
import heroMobile1 from "@assets/Home_Page_Hero_Mobile_-_Lawyer_1_900_1778705067349.webp";
import heroMobile2 from "@assets/Home_Page_Hero_Mobile_-_Lawyer_2_900_1778705067349.webp";
import heroMobile3 from "@assets/Home_Page_Hero_Mobile_-_Lawyer_3_900_1778705067349.webp";

const heroImages = [
  "hero-lawyer-1.webp",
  "hero-lawyer-2.webp",
  "hero-lawyer-3.webp",
];

const heroImagesMobile: { src: string; alt: string }[] = [
  { src: heroMobile1, alt: "Compliance officer reviewing communications for risk on a laptop" },
  { src: heroMobile2, alt: "Legal professional analyzing communications for unlawful content in real time" },
  { src: heroMobile3, alt: "Compliance analyst monitoring enterprise messages with HarmCheck" },
];

export function HeroSlideshowBackground({ decorative = false }: { decorative?: boolean }) {
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    const start = requestAnimationFrame(() => setActiveIndex(0));
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % Math.max(heroImages.length, heroImagesMobile.length));
    }, 8000);
    return () => {
      cancelAnimationFrame(start);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="absolute inset-0">
        {heroImages.map((img, i) => {
          const isActive = activeIndex === i;
          return (
            <picture
              key={img}
              className="absolute inset-0 block w-full h-full"
              style={{
                opacity: isActive ? 1 : 0,
                transform: isActive ? "scale(1.08)" : "scale(1)",
                transition: "opacity 1000ms ease, transform 14000ms linear",
              }}
            >
              <source media="(max-width: 767px)" srcSet={heroImagesMobile[i].src} />
              <img
                src={`${import.meta.env.BASE_URL}${img}`}
                alt={!decorative && isActive ? heroImagesMobile[i].alt : ""}
                aria-hidden={decorative || !isActive}
                loading={i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : "low"}
                decoding="async"
                className="w-full h-full object-cover object-center"
              />
            </picture>
          );
        })}
    </div>
  );
}

export function HeroSection() {
  return (
    <section className="relative pt-56 pb-10 md:pt-60 md:pb-14 border-b border-border/40 overflow-hidden">
      <HeroSlideshowBackground />
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-3xl">
          <h1 className="text-[54px] md:text-8xl font-bold tracking-tight mb-6 leading-[1.0] text-white">
            Know Risk.<br />
            Know Reward.
          </h1>

          <p className="text-[15px] text-white/80 mb-10 max-w-[590px] leading-snug md:leading-relaxed -mr-3 md:mr-0">
            <span className="text-[20px]">Detect harmful and unlawful communication by understanding <span className="sm:inline-block">meaning, not just words, in real time and at scale.</span></span>
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-12 md:mb-14 mt-8 md:mt-0">
            <Button size="lg" className="h-14 px-8 text-base rounded-none bg-primary hover:bg-primary/90 text-white border-primary group" asChild>
              <a href="/book-a-demo">
                Request a Demo
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>

          <div className="hidden md:flex mt-6 mb-6 md:mt-28 flex-col items-start gap-4 md:flex-row md:flex-nowrap md:items-center md:gap-8 opacity-90">
            <a href="/trust-security" className="flex items-center gap-2 no-underline text-white hover:text-white/80 transition-colors whitespace-nowrap">
              <ShieldCheck className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">SOC-2 Certified</span>
            </a>
            <a href="/trust-security" className="flex items-center gap-2 no-underline text-white hover:text-white/80 transition-colors whitespace-nowrap">
              <Zap className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">Enterprise-grade Security</span>
            </a>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <Target className="w-5 h-5 shrink-0" />
              <span className="text-sm font-medium">Industry-leading Accuracy</span>
            </div>
            <a href="https://www.forbes.com/councils/forbescoachescouncil/2025/03/12/ai-writing-tools-a-leadership-trend-thats-here-to-stay-but-for-better-or-worse/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 no-underline text-white hover:text-white/80 transition-colors whitespace-nowrap">
              <Newspaper className="w-5 h-5" />
              <span className="text-sm font-medium">As seen in Forbes</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
